import '@xyflow/react/dist/style.css';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
  type Connection,
  type OnNodeDrag,
  type NodeTypes,
  type EdgeTypes,
} from '@xyflow/react';
import { Box, Button, Flex, Heading, IconButton, Text } from '@chakra-ui/react';
import { RiAddFill, RiArrowLeftLine } from 'react-icons/ri';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import api from '@services/api';
import Loading from '@components/Loading';
import { useAuthContext } from '@contexts/auth';
import { useColorMode } from '@components/ui/color-mode';

import StageNode from './components/StageNode';
import TerminalNode from './components/TerminalNode';
import FunnelEdgeComponent from './components/FunnelEdge';
import NodePanel from './components/NodePanel';
import EdgePanel from './components/EdgePanel';
import CreateEdgeDialog from './components/CreateEdgeDialog';
import CreateStageDialog from './components/CreateStageDialog';
import DeleteStageDialog from './components/DeleteStageDialog';
import type { FunnelNode, FunnelEdge, WorkflowResponse, SidePanelMode } from './types';

const NODE_TYPES: NodeTypes = {
  stage: StageNode as NodeTypes[string],
  terminal: TerminalNode as NodeTypes[string],
};

const EDGE_TYPES: EdgeTypes = {
  funnel: FunnelEdgeComponent as EdgeTypes[string],
};

const TERMINAL_BASE_ID = 'terminal-base';
const TERMINAL_BLOCKED_ID = 'terminal-blocked';

const TERMINAL_DEFAULTS: FunnelNode[] = [
  {
    id: TERMINAL_BASE_ID,
    type: 'terminal',
    data: { label: 'Base Geral' },
    position: { x: 900, y: 80 },
    draggable: false,
  },
  {
    id: TERMINAL_BLOCKED_ID,
    type: 'terminal',
    data: { label: 'Bloqueado' },
    position: { x: 900, y: 240 },
    draggable: false,
  },
];

const FunnelEditor = () => {
  const navigate = useNavigate();
  const { checkIfHasRole } = useAuthContext();
  const { colorMode } = useColorMode();
  const canEdit = checkIfHasRole('CAN_MANAGE_SALESFUNNELS');

  const [nodes, setNodes, onNodesChange] = useNodesState<FunnelNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<FunnelEdge>([]);
  const [loading, setLoading] = useState(true);

  const [sidePanel, setSidePanel] = useState<SidePanelMode>(null);
  const [pendingConnection, setPendingConnection] = useState<{
    source: string;
    target: string;
  } | null>(null);
  const [showCreateStage, setShowCreateStage] = useState(false);
  const [deletingStage, setDeletingStage] = useState<{ id: string; name: string } | null>(null);

  const loadWorkflow = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<WorkflowResponse>('/sales_funnel/workflow');
      const { nodes: rawNodes, edges: rawEdges } = response.data;

      const serverTerminalIds = new Set(
        rawNodes.filter((n) => n.type === 'terminal').map((n) => n.id),
      );

      const flowNodes: FunnelNode[] = [
        ...(rawNodes.map((n) => ({
          ...n,
          draggable: n.type === 'stage',
        })) as FunnelNode[]),
        ...TERMINAL_DEFAULTS.filter((t) => !serverTerminalIds.has(t.id)),
      ];

      const pairCount: Record<string, number> = {};
      const pairIndex: Record<string, number> = {};
      for (const e of rawEdges) {
        const key = [e.source, e.target].sort().join('||');
        pairCount[key] = (pairCount[key] ?? 0) + 1;
      }

      const flowEdges: FunnelEdge[] = rawEdges.map((e) => {
        const key = [e.source, e.target].sort().join('||');
        const isParallel = pairCount[key] > 1;
        pairIndex[key] = (pairIndex[key] ?? 0) + 1;
        const nth = pairIndex[key];
        const curvature = isParallel ? (nth % 2 === 1 ? 0.5 : -0.5) : 0.25;
        const labelOffset = isParallel ? (nth % 2 === 1 ? -20 : 20) : 0;

        return {
          id: e.id,
          source: e.source,
          target: e.target,
          label: e.label,
          data: { dispositionOptionId: String(e.disposition_option_id), curvature, labelOffset },
          markerEnd: { type: MarkerType.ArrowClosed },
          style: { strokeWidth: 2 },
          type: 'funnel',
        };
      });

      setNodes(flowNodes);
      setEdges(flowEdges);
    } catch (error) {
      toast.error('Erro ao carregar o funil.');
      datadogRum.addError(new Error('Fail to load sales funnel workflow'), { error });
    } finally {
      setLoading(false);
    }
  }, [setNodes, setEdges]);

  useEffect(() => {
    loadWorkflow();
  }, [loadWorkflow]);

  const saveNodePosition = useCallback(
    async (node: FunnelNode) => {
      if (node.type !== 'stage') return;
      try {
        await api.put(`/sales_funnel/${node.id.replace('funnel-', '')}`, {
          position_x: Math.round(node.position.x),
          position_y: Math.round(node.position.y),
        });
      } catch (error) {
        datadogRum.addError(new Error('Fail to save node position'), {
          error,
          nodeId: node.id,
        });
      }
    },
    [],
  );

  const onNodeDragStop: OnNodeDrag<FunnelNode> = useCallback(
    (_event, _node, nodes) => {
      if (!canEdit) return;
      Promise.all(nodes.map(saveNodePosition));
    },
    [canEdit, saveNodePosition],
  );

  const onBeforeDelete = useCallback(
    async ({ nodes: toDelete }: { nodes: FunnelNode[] }) => {
      if (!canEdit) return false;
      const stage = toDelete.find((n) => n.type === 'stage');
      if (stage) {
        setDeletingStage({
          id: stage.id.replace('funnel-', ''),
          name: String((stage.data as { label: string }).label),
        });
      }
      // Sempre cancela a remoção nativa do React Flow: a exclusão de etapas
      // deve passar pelo diálogo validado (mover clientes + persistir no backend).
      return false;
    },
    [canEdit],
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      if (!canEdit) return;
      const source = connection.source ?? '';
      const target = connection.target ?? '';

      if (!source || !target || source.startsWith('terminal')) return;

      setPendingConnection({ source, target });
    },
    [canEdit],
  );

  const onEdgeCreated = useCallback(
    (dispositionOptionId: string, label: string) => {
      if (!pendingConnection) return;
      const { source, target } = pendingConnection;

      const key = [source, target].sort().join('||');
      const parallelEdges = edges.filter(
        (e) => [e.source, e.target].sort().join('||') === key,
      );
      const isParallel = parallelEdges.length > 0;
      const nth = parallelEdges.length + 1;
      const curvature = isParallel ? (nth % 2 === 1 ? 0.5 : -0.5) : 0.25;
      const labelOffset = isParallel ? (nth % 2 === 1 ? -20 : 20) : 0;

      const newEdge: FunnelEdge = {
        id: `d-${dispositionOptionId}`,
        source,
        target,
        label,
        data: { dispositionOptionId, curvature, labelOffset },
        markerEnd: { type: MarkerType.ArrowClosed },
        style: { strokeWidth: 2 },
        type: 'funnel',
      };

      setEdges((prev) => addEdge(newEdge, prev));
      setPendingConnection(null);
    },
    [pendingConnection, setEdges],
  );

  const nextStageOrder = useMemo(() => {
    const orders = nodes
      .filter((n): n is FunnelNode & { data: { order: number } } => n.type === 'stage')
      .map((n) => Number(n.data.order ?? 0));
    return orders.length ? Math.max(...orders) + 1 : 1;
  }, [nodes]);

  const handleStageCreated = useCallback(async () => {
    setShowCreateStage(false);
    await loadWorkflow();
  }, [loadWorkflow]);

  const stageOptions = useMemo(
    () =>
      nodes
        .filter((n) => n.type === 'stage')
        .map((n) => ({
          id: n.id.replace('funnel-', ''),
          name: String((n.data as { label: string }).label),
        })),
    [nodes],
  );

  const handleStageDeleted = useCallback(async () => {
    setDeletingStage(null);
    setSidePanel(null);
    await loadWorkflow();
  }, [loadWorkflow]);

  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: FunnelNode) => {
      if (!canEdit || node.type !== 'stage') return;
      setSidePanel({ type: 'node', nodeId: node.id });
    },
    [canEdit],
  );

  const onEdgeClick = useCallback(
    (_event: React.MouseEvent, edge: FunnelEdge) => {
      if (!canEdit) return;
      setSidePanel({ type: 'edge', edgeId: edge.id });
    },
    [canEdit],
  );

  const onPaneClick = useCallback(() => {
    setSidePanel(null);
  }, []);

  const selectedNode = useMemo(() => {
    if (sidePanel?.type !== 'node') return null;
    return nodes.find((n) => n.id === sidePanel.nodeId) ?? null;
  }, [sidePanel, nodes]);

  const selectedEdge = useMemo(() => {
    if (sidePanel?.type !== 'edge') return null;
    return edges.find((e) => e.id === sidePanel.edgeId) ?? null;
  }, [sidePanel, edges]);

  const funnelIdFromNodeId = (nodeId: string) => nodeId.replace('funnel-', '');

  if (loading) return <Loading />;

  return (
    <Box h="calc(100vh - 120px)" display="flex" flexDirection="column">
      <Flex justifyContent="space-between" alignItems="center" mb={3}>
        <Flex alignItems="center" gap={2}>
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => navigate('/painel/funil-de-vendas')}
          >
            <RiArrowLeftLine />
          </IconButton>
          <Heading size="md">Editor do Funil</Heading>
        </Flex>
        {canEdit && (
          <Button size="sm" onClick={() => setShowCreateStage(true)}>
            <RiAddFill /> Nova etapa
          </Button>
        )}
      </Flex>

      <Box
        flex={1}
        position="relative"
        border="1px solid"
        borderColor="border.muted"
        borderRadius="md"
        overflow="hidden"
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStop={onNodeDragStop}
          onConnect={onConnect}
          onBeforeDelete={onBeforeDelete}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          onPaneClick={onPaneClick}
          nodeTypes={NODE_TYPES}
          edgeTypes={EDGE_TYPES}
          colorMode={colorMode}
          fitView
          nodesDraggable={canEdit}
          nodesConnectable={canEdit}
          elementsSelectable={canEdit}
          defaultEdgeOptions={{
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { strokeWidth: 2 },
          }}
        >
          <Background />
          <Controls />
          <MiniMap />
        </ReactFlow>

        {sidePanel?.type === 'node' && selectedNode && selectedNode.type === 'stage' && (
          <NodePanel
            funnelId={funnelIdFromNodeId(selectedNode.id)}
            funnelName={String((selectedNode.data as { label: string }).label)}
            funnelOrder={Number((selectedNode.data as { order: number }).order)}
            onClose={() => setSidePanel(null)}
            onRenamed={(newName) => {
              setNodes((prev) =>
                prev.map((n) =>
                  n.id === selectedNode.id
                    ? { ...n, data: { ...n.data, label: newName } }
                    : n,
                ) as FunnelNode[],
              );
            }}
            onReordered={(newOrder) => {
              setNodes((prev) =>
                prev.map((n) =>
                  n.id === selectedNode.id
                    ? { ...n, data: { ...n.data, order: newOrder } }
                    : n,
                ) as FunnelNode[],
              );
            }}
            onDataChanged={loadWorkflow}
            onRequestDelete={() =>
              setDeletingStage({
                id: funnelIdFromNodeId(selectedNode.id),
                name: String((selectedNode.data as { label: string }).label),
              })
            }
          />
        )}

        {sidePanel?.type === 'edge' && selectedEdge && (
          <EdgePanel
            edgeId={selectedEdge.id}
            dispositionOptionId={String(selectedEdge.data?.dispositionOptionId ?? '')}
            label={String(selectedEdge.label ?? '')}
            sourceFunnelId={funnelIdFromNodeId(selectedEdge.source)}
            onClose={() => setSidePanel(null)}
            onRenamed={(newLabel) => {
              setEdges((prev) =>
                prev.map((e) =>
                  e.id === selectedEdge.id ? { ...e, label: newLabel } : e,
                ),
              );
              setSidePanel(null);
            }}
            onDeleted={() => {
              setEdges((prev) => prev.filter((e) => e.id !== selectedEdge.id));
              setSidePanel(null);
            }}
          />
        )}

        {!canEdit && (
          <Box
            position="absolute"
            bottom={4}
            left="50%"
            transform="translateX(-50%)"
            bg="blackAlpha.700"
            color="white"
            px={3}
            py={1}
            borderRadius="md"
            pointerEvents="none"
          >
            <Text fontSize="xs">Modo visualização — sem permissão de edição</Text>
          </Box>
        )}
      </Box>

      {showCreateStage && (
        <CreateStageDialog
          nextOrder={nextStageOrder}
          onClose={() => setShowCreateStage(false)}
          onCreated={handleStageCreated}
        />
      )}

      {deletingStage && (
        <DeleteStageDialog
          funnelId={deletingStage.id}
          funnelName={deletingStage.name}
          stages={stageOptions}
          onClose={() => setDeletingStage(null)}
          onDeleted={handleStageDeleted}
        />
      )}

      {pendingConnection && (
        <CreateEdgeDialog
          sourceFunnelId={funnelIdFromNodeId(pendingConnection.source)}
          targetFunnelId={pendingConnection.target}
          onClose={() => setPendingConnection(null)}
          onCreated={onEdgeCreated}
        />
      )}
    </Box>
  );
};

export default FunnelEditor;
